import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RotateCw, Search } from "lucide-react";

export default function Communications() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [retrying, setRetrying] = useState({});

  const load = async () => {
    const params = status ? { status } : {};
    const { data } = await api.get("/communications", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [status]);

  const retry = async (id) => {
    setRetrying((r) => ({ ...r, [id]: true }));
    try {
      const { data } = await api.post(`/communications/${id}/retry`);
      if (data?.status === "sent") toast.success("Retry succeeded");
      else toast.error(`Retry failed: ${data?.error?.slice(0, 120) || "unknown"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRetrying((r) => ({ ...r, [id]: false })); }
  };

  const retryAllFailed = async () => {
    if (!window.confirm("Retry all failed messages via the server bulk endpoint?")) return;
    try {
      const { data } = await api.post("/communications/retry-all");
      toast.success(`Sent ${data.sent}/${data.total} · Still failed ${data.failed}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const failedCount = items.filter((c) => c.status === "failed").length;

  return (
    <div data-testid="page-communications" className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Log</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Communications</h1>
        </div>
        <div className="flex items-center gap-3">
          {failedCount > 0 && (
            <button data-testid="btn-retry-all" onClick={retryAllFailed} className="bg-[var(--amber)] hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-md flex items-center gap-2">
              <RotateCw size={14} /> Retry {failedCount} Failed
            </button>
          )}
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm">
            <option value="">All</option><option value="sent">Sent</option><option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 max-w-xl">
        <Search size={14} className="text-slate-400" />
        <input
          data-testid="input-comms-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search dispatch, channel, contact, to, error…"
          className="flex-1 outline-none text-sm bg-transparent"
        />
        {q && <button onClick={() => setQ("")} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
              <th className="px-5 py-3">Dispatch</th><th className="px-5 py-3">Channel</th><th className="px-5 py-3">Contact</th>
              <th className="px-5 py-3">To</th><th className="px-5 py-3">Sent</th><th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Error</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const qq = q.trim().toLowerCase();
              const visible = qq
                ? items.filter((c) => [c.dispatch_code, c.channel, c.contact_name, c.to_address, c.status, c.error]
                    .filter(Boolean).some((v) => String(v).toLowerCase().includes(qq)))
                : items;
              return (<>
              {visible.map((c) => (
              <tr key={c.id} data-testid={`comm-row-${c.id}`} className="table-row border-t border-slate-100">
                <td className="px-5 py-3 font-mono text-xs">{c.dispatch_code}</td>
                <td className="px-5 py-3"><span className="text-xs uppercase font-semibold bg-slate-100 px-2 py-0.5 rounded-full">{c.channel}</span></td>
                <td className="px-5 py-3">{c.contact_name}</td>
                <td className="px-5 py-3 font-mono text-xs">{c.to_address}</td>
                <td className="px-5 py-3 text-xs text-slate-500">{new Date(c.sent_at).toLocaleString()}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold ${c.status === "sent" ? "text-emerald-700" : "text-rose-700"}`}>{c.status}</span>
                    {c.cc && c.cc.length > 0 && (
                      <span title={`CC: ${c.cc.join(", ")}`} className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">CC {c.cc.length}</span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-xs text-slate-500 max-w-xs truncate" title={c.error || ""}>{c.error?.slice(0, 60) || "-"}</td>
                <td className="px-5 py-3 text-right">
                  {c.status === "failed" && (
                    <button data-testid={`btn-retry-${c.id}`} disabled={retrying[c.id]} onClick={() => retry(c.id)} className="text-xs text-amber-700 hover:text-amber-900 font-semibold flex items-center gap-1 disabled:opacity-50">
                      <RotateCw size={12} className={retrying[c.id] ? "animate-spin" : ""} /> Retry
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-slate-500">{q ? `No matches for "${q}"` : "No communications logged"}</td></tr>}
            </>);
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
